import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CloseIcon from '@mui/icons-material/Close';
import { SAGE, CREAM } from '../theme';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';
import useAuth from '../hooks/useAuth';
import {
  saveSignupDraft,
  completeSignup,
  uploadResumeForSignup,
  trackLinkedinClick,
} from '../services/signupService';

// Step order, used for indicator and navigation.
const STEPS = [
  { key: 'name', label: 'About you' },
  { key: 'intent', label: 'Where you are on the road' },
  { key: 'intent_detail', label: 'A little more' },
  { key: 'timeline', label: 'Your timeline' },
  { key: 'markets', label: 'Where you want to land' },
  { key: 'notes', label: 'Anything else?' },
];

const INTENT_OPTIONS = [
  { value: 'advance', label: 'Advance in my current field' },
  { value: 'pivot', label: 'Pivot my career' },
  { value: 'new_position', label: 'Look for a new position' },
  { value: 'other', label: 'Other' },
];

const TIMELINE_OPTIONS = [
  { value: 'asap', label: 'ASAP' },
  { value: '6_months', label: 'Within 6 months' },
  { value: '1_2_years', label: '1–2 years out' },
  { value: 'exploring', label: 'Just exploring' },
];

const INTENT_PROMPTS = {
  advance: "What does 'advancing' look like for you? (e.g., moving into management, staying IC but senior, specific skills you want to grow)",
  pivot: "Where are you hoping to pivot from/to? What's driving the change?",
  new_position: "What's pushing the search? (e.g., comp, growth, culture, laid off)",
  other: "Tell us a bit about what you're trying to do — we'd love to understand your use case and see if we can help.",
};

const PERSONA_LABELS = {
  seeker: 'Seeker',
  explorer: 'Explorer',
  pivoter: 'Pivoter',
  climber: 'Climber',
};

// Map landing fork-road picks → SignupFlow form fields.
// LandingForkRoadPage stores `onward_journey = { persona, fork2, fork3 }`;
// the meaning of fork2/fork3 differs per persona (see PERSONAS in
// LandingForkRoadPage). Mapping is best-effort — gaps just leave fields
// empty and the user fills them in.
const PERSONA_TO_INTENT = {
  seeker: 'new_position',
  explorer: 'other',
  pivoter: 'pivot',
  climber: 'advance',
};

const SEEKER_FORK2_TIMELINE = { week: 'asap', month: 'asap', quarter: '6_months' };
const CLIMBER_FORK2_TIMELINE = { '6mo': '6_months', '18mo': '1_2_years', long: '1_2_years' };
const EXPLORER_FORK3_TIMELINE = { months: '6_months', year: '1_2_years', someday: 'exploring' };

const FORK_LABELS = {
  // seeker
  week: 'this week', month: 'this month', quarter: 'this quarter',
  same: 'similar to my last role', adjacent: 'adjacent but different', open: 'open to what fits',
  // explorer
  field: 'change in field', stage: 'change in stage', curious: 'just curious',
  someday: 'someday',
  // pivoter
  specific: 'I know the role', roughly: "direction's clear, details aren't", notyet: 'I want change but not sure to what',
  feasible: 'is this pivot feasible?', path: "what's the actual path?", identity: 'am I really that person?',
  // climber
  '6mo': '6 months', '18mo': '18 months', long: 'long game (3–5 yrs)',
  skills: 'skills or scope', visibility: 'visibility', network: 'network & opportunity',
  // shared
  months: 'next few months', year: 'sometime this year',
};

const composeJourneyNotes = ({ persona, fork2, fork3 }) => {
  const lines = [];
  if (fork2 && FORK_LABELS[fork2]) lines.push(`From the landing page: ${FORK_LABELS[fork2]}.`);
  if (fork3 && FORK_LABELS[fork3]) lines.push(`Bottleneck / focus: ${FORK_LABELS[fork3]}.`);
  return lines.join(' ');
};

const mapJourneyToSignup = ({ persona, fork2, fork3 } = {}) => {
  const intent = PERSONA_TO_INTENT[persona] || null;
  let timeline = null;
  if (persona === 'seeker') timeline = SEEKER_FORK2_TIMELINE[fork2] || null;
  else if (persona === 'climber') timeline = CLIMBER_FORK2_TIMELINE[fork2] || null;
  else if (persona === 'explorer') timeline = EXPLORER_FORK3_TIMELINE[fork3] || null;
  // pivoter has no timeline fork
  const intent_detail = composeJourneyNotes({ persona, fork2, fork3 });
  return { intent, timeline, intent_detail };
};

const EMPTY_STATE = {
  name: '',
  preferred_name: '',
  persona: null,             // captured pre-signup at the Fork Road landing
  intent: null,
  intent_detail: '',
  timeline: null,
  markets: [],
  notes: '',
  product_updates_opt_in: false,
  notify_on_market_launch: false,  // checkbox shown when user picks non-served markets
  signup_method: 'manual',
  resume_uploaded_at: null,
};

const SignupFlow = ({ editMode = false, onDone }) => {
  const { account, refresh, handleLogout } = useAuth();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_STATE);
  const [marketsTaxonomy, setMarketsTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resumeParsing, setResumeParsing] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  // Prefill from existing account (WorkOS name on first login, or saved
  // signup_data if user is editing after completion or resuming a draft).
  // Persona comes from sessionStorage (set by the Fork Road landing) on
  // first-time signups; persisted in saved.persona on edits.
  useEffect(() => {
    const prefill = { ...EMPTY_STATE };
    if (account?.name) prefill.name = account.name;
    if (account?.preferred_name) prefill.preferred_name = account.preferred_name;
    if (account?.product_updates_opt_in) prefill.product_updates_opt_in = true;
    if (account?.signup_method) prefill.signup_method = account.signup_method;
    const saved = account?.signup_data || {};
    if (saved.persona) prefill.persona = saved.persona;
    if (saved.intent) prefill.intent = saved.intent;
    if (saved.intent_detail) prefill.intent_detail = saved.intent_detail;
    if (saved.timeline) prefill.timeline = saved.timeline;
    if (Array.isArray(saved.markets)) prefill.markets = saved.markets;
    if (saved.notes) prefill.notes = saved.notes;
    if (saved.notify_on_market_launch) prefill.notify_on_market_launch = true;
    if (saved.resume_uploaded_at) prefill.resume_uploaded_at = saved.resume_uploaded_at;
    // First-time only: pull persona + the rest of the landing journey
    // (fork2/fork3 picks) from sessionStorage. Map the picks to intent /
    // timeline / intent_detail so the user doesn't re-answer questions
    // they already answered on the landing page. Clear after consumption
    // so picks don't leak into a different account on logout.
    if (!prefill.persona) {
      try {
        const fromSession = sessionStorage.getItem('onward_persona');
        if (fromSession && PERSONA_LABELS[fromSession]) {
          prefill.persona = fromSession;
        }
        sessionStorage.removeItem('onward_persona');
      } catch { /* sessionStorage might be unavailable */ }
    }
    try {
      const journeyRaw = sessionStorage.getItem('onward_journey');
      if (journeyRaw) {
        const journey = JSON.parse(journeyRaw);
        const mapped = mapJourneyToSignup(journey);
        if (!prefill.intent && mapped.intent) prefill.intent = mapped.intent;
        if (!prefill.timeline && mapped.timeline) prefill.timeline = mapped.timeline;
        if (!prefill.intent_detail && mapped.intent_detail) {
          prefill.intent_detail = mapped.intent_detail;
        }
        sessionStorage.removeItem('onward_journey');
      }
    } catch { /* malformed JSON or sessionStorage unavailable */ }
    setForm(prefill);

    fetch(`${API_URL}/api/taxonomy/markets`, {
      headers: { ...mockAuthHeaders() },
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) => setMarketsTaxonomy(data))
      .catch((err) => console.error('Failed to load markets:', err))
      .finally(() => setLoading(false));
    // Intentionally only on mount / when account first loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.workos_user_id]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const toggleMarket = (id) =>
    setForm((prev) => ({
      ...prev,
      markets: prev.markets.includes(id)
        ? prev.markets.filter((m) => m !== id)
        : [...prev.markets, id],
    }));

  // Autosave on Next — best effort, non-blocking.
  const persistDraft = useCallback(async () => {
    const patch = {
      name: form.name || null,
      preferred_name: form.preferred_name || null,
      signup_data: {
        persona: form.persona,
        intent: form.intent,
        intent_detail: form.intent_detail,
        timeline: form.timeline,
        markets: form.markets,
        notes: form.notes,
        notify_on_market_launch: form.notify_on_market_launch,
        resume_uploaded_at: form.resume_uploaded_at,
      },
    };
    try {
      await saveSignupDraft(patch);
    } catch (err) {
      console.warn('draft save failed:', err);
    }
  }, [form]);

  const canAdvance = () => {
    switch (STEPS[step].key) {
      case 'name': return form.name.trim().length > 0;
      case 'intent': return form.intent != null;
      case 'intent_detail': return (form.intent_detail || '').trim().length > 0 || form.intent === 'advance';
      case 'timeline': return form.timeline != null;
      case 'markets': return form.markets.length > 0;
      case 'notes': return true;
      default: return true;
    }
  };

  const next = async () => {
    await persistDraft();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await completeSignup({
        name: form.name.trim(),
        preferred_name: form.preferred_name?.trim() || null,
        signup_method: form.signup_method,
        persona: form.persona,
        intent: form.intent,
        intent_detail: form.intent_detail || null,
        timeline: form.timeline,
        markets: form.markets,
        notes: form.notes || null,
        product_updates_opt_in: form.product_updates_opt_in,
        notify_on_market_launch: form.notify_on_market_launch,
        resume_uploaded_at: form.resume_uploaded_at,
      });
      if (editMode) {
        await refresh();
        if (onDone) onDone();
      } else {
        // Don't refresh — that would route the user to PendingPage. We're a
        // private beta: thank them, then log out so they land back on the
        // public landing page.
        setShowThankYou(true);
      }
    } catch (err) {
      console.error('signup submit failed:', err);
      setSnackbar({ severity: 'error', message: 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Thank-you modal closes by logging the user out, which clears local state
  // and redirects to "/" (the landing page) via handleLogout in useAuth.
  const dismissThankYou = () => { handleLogout(); };

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setResumeParsing(true);
    try {
      const result = await uploadResumeForSignup(file);
      // Only prefill name if user hasn't typed one. Never overwrite input.
      const parsedName = result?.personal_info?.name || result?.name;
      setForm((prev) => ({
        ...prev,
        name: prev.name || parsedName || '',
        resume_uploaded_at: new Date().toISOString(),
        signup_method: 'resume',
      }));
      setSnackbar({ severity: 'success', message: 'Resume parsed. Your profile will be ready after admission.' });
    } catch (err) {
      console.error('resume parse failed:', err);
      setSnackbar({
        severity: 'error',
        message: 'Resume parse failed, but you can still complete signup without it.',
      });
    } finally {
      setResumeParsing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: CREAM[100], py: { xs: 2, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Header: step indicator + edit-mode close */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900] }}>
            {editMode
              ? 'Update your answers'
              : form.persona && PERSONA_LABELS[form.persona]
                ? `Welcome, ${PERSONA_LABELS[form.persona]} — tell us your story.`
                : 'Tell us your story.'}
          </Typography>
          {editMode && onDone && (
            <IconButton onClick={onDone} size="small"><CloseIcon /></IconButton>
          )}
        </Box>

        <Box sx={{ mb: 4 }}>
          <LinearProgress
            variant="determinate"
            value={((step + 1) / STEPS.length) * 100}
            sx={{ height: 6, borderRadius: 3, bgcolor: CREAM[200], '& .MuiLinearProgress-bar': { bgcolor: SAGE[600] } }}
          />
          <Typography sx={{ mt: 1, fontSize: '0.75rem', color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
            Step {step + 1} of {STEPS.length}: {STEPS[step].label}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          {/* Main column */}
          <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }} elevation={1}>
            {STEPS[step].key === 'name' && (
              <StepName form={form} update={update} />
            )}
            {STEPS[step].key === 'intent' && (
              <StepIntent form={form} update={update} />
            )}
            {STEPS[step].key === 'intent_detail' && (
              <StepIntentDetail form={form} update={update} />
            )}
            {STEPS[step].key === 'timeline' && (
              <StepTimeline form={form} update={update} />
            )}
            {STEPS[step].key === 'markets' && (
              <StepMarkets
                form={form}
                update={update}
                toggleMarket={toggleMarket}
                marketsTaxonomy={marketsTaxonomy}
              />
            )}
            {STEPS[step].key === 'notes' && (
              <StepNotes form={form} update={update} />
            )}

            {/* Nav */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={back}
                disabled={step === 0 || submitting}
                sx={{ textTransform: 'none', color: SAGE[500] }}
              >
                Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button
                  endIcon={<ArrowForwardIcon />}
                  onClick={next}
                  disabled={!canAdvance()}
                  variant="contained"
                  sx={{ bgcolor: SAGE[700], textTransform: 'none', '&:hover': { bgcolor: SAGE[800] } }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                  onClick={submit}
                  disabled={submitting || !canAdvance()}
                  variant="contained"
                  sx={{ bgcolor: SAGE[700], textTransform: 'none', '&:hover': { bgcolor: SAGE[800] } }}
                >
                  {submitting ? 'Sending' : (editMode ? 'Save changes' : 'Request an invitation')}
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Side column: resume + LinkedIn */}
          <Box>
            <ResumeSidePanel
              uploadedAt={form.resume_uploaded_at}
              parsing={resumeParsing}
              onUpload={handleResumeUpload}
            />
            <LinkedInSidePanel />
          </Box>
        </Box>

        {!editMode && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button size="small" onClick={handleLogout} sx={{ color: SAGE[400], textTransform: 'none' }}>
              Sign out
            </Button>
          </Box>
        )}
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity}>{snackbar.message}</Alert>}
      </Snackbar>

      <Dialog
        open={showThankYou}
        disableEscapeKeyDown
        onClose={(e, reason) => {
          // Backdrop clicks are ignored — the only way out is the button,
          // which logs the user out and lands them on the public landing.
          if (reason !== 'backdropClick') dismissThankYou();
        }}
        PaperProps={{ sx: { borderRadius: 3, bgcolor: CREAM[50], px: 1, py: 1, maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400, fontSize: '1.75rem', color: SAGE[900], pb: 0.5 }}>
          Thank you.
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: SAGE[700], fontSize: '1rem', lineHeight: 1.55 }}>
            We'll be reaching out soon. Onward is a private beta — your story is safe with us, and we'll be in touch as we open up access.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={dismissThankYou}
            variant="contained"
            sx={{
              bgcolor: SAGE[800],
              color: CREAM[50],
              textTransform: 'none',
              borderRadius: 999,
              px: 3,
              '&:hover': { bgcolor: SAGE[900] },
            }}
          >
            Back to home
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// -------- Step components --------

const StepName = ({ form, update }) => (
  <Box>
    <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}>
      Let's start with you.
    </Typography>
    <Typography variant="body2" sx={{ color: SAGE[500], mb: 3 }}>
      What should we call you? Set a preferred name and we'll use it everywhere.
    </Typography>
    <Stack spacing={2}>
      <TextField
        label="Full name"
        fullWidth
        value={form.name}
        onChange={(e) => update({ name: e.target.value })}
        autoFocus
      />
      <TextField
        label="Preferred name (optional)"
        fullWidth
        value={form.preferred_name}
        onChange={(e) => update({ preferred_name: e.target.value })}
        helperText="If set, we'll use this instead of your first name."
      />
    </Stack>
  </Box>
);

const StepIntent = ({ form, update }) => (
  <Box>
    <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}>
      Where are you on the road?
    </Typography>
    <Typography variant="body2" sx={{ color: SAGE[500], mb: 3 }}>
      Pick the path that's yours — we'll meet you there.
    </Typography>
    <Stack spacing={1.5}>
      {INTENT_OPTIONS.map((opt) => (
        <ChoiceRow
          key={opt.value}
          label={opt.label}
          selected={form.intent === opt.value}
          onClick={() => update({ intent: opt.value })}
        />
      ))}
    </Stack>
  </Box>
);

const StepIntentDetail = ({ form, update }) => {
  const prompt = INTENT_PROMPTS[form.intent] || 'Tell us a bit more about what you’re looking for.';
  return (
    <Box>
      <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}>
        {prompt}
      </Typography>
      <Typography variant="body2" sx={{ color: SAGE[500], mb: 3 }}>
        {form.intent === 'advance' ? 'Optional — but the more you tell us, the better we can shape what comes next.' : 'A couple of sentences is plenty. Your words, not ours.'}
      </Typography>
      <TextField
        multiline
        minRows={5}
        fullWidth
        value={form.intent_detail}
        onChange={(e) => update({ intent_detail: e.target.value })}
      />
    </Box>
  );
};

const StepTimeline = ({ form, update }) => (
  <Box>
    <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}>
      What's your timeline?
    </Typography>
    <Typography variant="body2" sx={{ color: SAGE[500], mb: 3 }}>
      We'll calibrate the work to your runway.
    </Typography>
    <Stack spacing={1.5}>
      {TIMELINE_OPTIONS.map((opt) => (
        <ChoiceRow
          key={opt.value}
          label={opt.label}
          selected={form.timeline === opt.value}
          onClick={() => update({ timeline: opt.value })}
        />
      ))}
    </Stack>
  </Box>
);

const StepMarkets = ({ form, update, toggleMarket, marketsTaxonomy }) => {
  const markets = marketsTaxonomy?.markets || [];
  const groups = [
    { label: 'Remote', items: markets.filter((m) => m.type === 'remote') },
    {
      label: 'US Metros',
      items: markets.filter((m) => m.type === 'metro' && m.region_id === 'us'),
    },
    {
      label: 'Canadian Metros',
      items: markets.filter((m) => m.type === 'metro' && m.region_id === 'ca'),
    },
    {
      label: 'Other',
      items: markets.filter((m) => m.type === 'other' || m.type === 'international'),
    },
  ];

  const selected = form.markets;
  const servedSelected = markets.some((m) => m.served && selected.includes(m.id));
  const hasAnySelection = selected.length > 0;
  const onlyUnserved = hasAnySelection && !servedSelected;
  const unservedSelectedNames = markets
    .filter((m) => !m.served && selected.includes(m.id))
    .map((m) => m.name);
  const hasUnservedSelected = unservedSelectedNames.length > 0;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}>
        Where are you hoping to land?
      </Typography>
      <Typography variant="body2" sx={{ color: SAGE[500], mb: 2 }}>
        Pick any that apply.
      </Typography>
      <Box sx={{ p: 2, bgcolor: CREAM[200], borderRadius: 2, mb: 3 }}>
        <Typography variant="body2" sx={{ color: SAGE[700], fontSize: '0.85rem' }}>
          We currently have the strongest coverage in <b>Seattle Metro</b> and <b>US Remote</b>.
          If your target markets are elsewhere, you'll join our waitlist — we'll reach out as we expand.
        </Typography>
      </Box>

      <Stack spacing={2}>
        {groups.filter((g) => g.items.length > 0).map((g) => (
          <Box key={g.label}>
            <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {g.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {g.items.map((m) => (
                <SignupChip
                  key={m.id}
                  label={m.name + (m.served ? ' ★' : '')}
                  selected={selected.includes(m.id)}
                  onClick={() => toggleMarket(m.id)}
                />
              ))}
            </Box>
          </Box>
        ))}
        {markets.length === 0 && (
          <Typography variant="body2" sx={{ color: SAGE[500], fontStyle: 'italic' }}>
            Loading markets…
          </Typography>
        )}
      </Stack>

      {onlyUnserved && (
        <Box sx={{ mt: 3, p: 2, bgcolor: '#FFF4E5', borderRadius: 2, border: '1px solid #FFDAA5' }}>
          <Typography variant="body2" sx={{ color: '#7A4F00', fontSize: '0.85rem' }}>
            Your selections are outside our currently-served markets. You can still sign up — we'll reach
            out as we expand coverage.
          </Typography>
        </Box>
      )}

      {hasUnservedSelected && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.notify_on_market_launch}
                onChange={(e) => update({ notify_on_market_launch: e.target.checked })}
                sx={{ color: SAGE[500], '&.Mui-checked': { color: SAGE[700] } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: SAGE[700] }}>
                Notify me when Onward launches in {unservedSelectedNames.join(', ')}.
              </Typography>
            }
          />
        </Box>
      )}
    </Box>
  );
};

const StepNotes = ({ form, update }) => (
  <Box>
    <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}>
      Anything else we should know?
    </Typography>
    <Typography variant="body2" sx={{ color: SAGE[500], mb: 3 }}>
      Optional. What's been hardest about your search? What would help most?
    </Typography>
    <TextField
      multiline
      minRows={4}
      fullWidth
      value={form.notes}
      onChange={(e) => update({ notes: e.target.value })}
    />
    <FormControlLabel
      sx={{ mt: 2 }}
      control={
        <Checkbox
          checked={form.product_updates_opt_in}
          onChange={(e) => update({ product_updates_opt_in: e.target.checked })}
        />
      }
      label="Send me occasional product updates (optional, you can unsubscribe any time)"
    />
  </Box>
);

// -------- Side panels --------

const ResumeSidePanel = ({ uploadedAt, parsing, onUpload }) => {
  const inputRef = useRef(null);
  const openPicker = () => inputRef.current?.click();
  return (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }} elevation={1}>
      <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 1 }}>
        Skip ahead
      </Typography>
      <Typography variant="body2" sx={{ color: SAGE[500], fontSize: '0.85rem', mb: 2 }}>
        Upload your resume — we'll extract everything. Optional, but it helps us prioritize you for early access.
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.txt"
        style={{ display: 'none' }}
        onChange={(e) => onUpload(e.target.files?.[0])}
      />
      {uploadedAt ? (
        <Box sx={{ p: 2, bgcolor: SAGE[50], borderRadius: 2, border: `1px solid ${SAGE[300]}` }}>
          <Typography variant="body2" sx={{ color: SAGE[700], fontSize: '0.85rem' }}>
            ✓ Resume uploaded. Nice.
          </Typography>
          <Button size="small" sx={{ mt: 1, textTransform: 'none', color: SAGE[600] }} onClick={openPicker} disabled={parsing}>
            Replace
          </Button>
        </Box>
      ) : (
        <Button
          fullWidth
          startIcon={parsing ? <CircularProgress size={16} /> : <UploadFileIcon />}
          onClick={openPicker}
          disabled={parsing}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderColor: SAGE[400],
            color: SAGE[700],
            py: 1.5,
            '&:hover': { borderColor: SAGE[600], bgcolor: SAGE[50] },
          }}
        >
          {parsing ? 'Parsing…' : 'Upload resume'}
        </Button>
      )}
    </Paper>
  );
};

const LinkedInSidePanel = () => {
  const [clicked, setClicked] = useState(false);
  const onClick = () => {
    trackLinkedinClick();
    setClicked(true);
  };
  return (
    <Paper sx={{ p: 3, borderRadius: 3, opacity: 0.95 }} elevation={0}>
      <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 1, fontSize: '0.95rem' }}>
        Or sync with LinkedIn
      </Typography>
      <Typography variant="body2" sx={{ color: SAGE[500], fontSize: '0.82rem', mb: 2 }}>
        Import your professional profile directly from LinkedIn.
      </Typography>
      <Button
        fullWidth
        startIcon={<LinkedInIcon />}
        onClick={onClick}
        disabled={clicked}
        variant="outlined"
        sx={{
          textTransform: 'none',
          borderColor: SAGE[300],
          color: SAGE[500],
          '&:hover': { borderColor: SAGE[500], bgcolor: CREAM[100] },
        }}
      >
        {clicked ? 'Thanks — we\'ll let you know' : 'Connect LinkedIn (Coming soon)'}
      </Button>
    </Paper>
  );
};

// -------- Shared UI bits --------

const ChoiceRow = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: 2,
      cursor: 'pointer',
      border: `1.5px solid ${selected ? SAGE[600] : SAGE[300]}`,
      bgcolor: selected ? SAGE[50] : 'transparent',
      transition: 'all 0.15s',
      '&:hover': { borderColor: SAGE[500], bgcolor: CREAM[100] },
    }}
  >
    <Typography sx={{ fontFamily: "'Outfit', sans-serif", color: selected ? SAGE[800] : SAGE[700], fontWeight: selected ? 600 : 400 }}>
      {label}
    </Typography>
  </Box>
);

const SignupChip = ({ label, selected, onClick }) => (
  <Chip
    label={label}
    size="small"
    onClick={onClick}
    sx={{
      fontFamily: "'Outfit', sans-serif",
      fontSize: '0.8rem',
      height: 30,
      cursor: 'pointer',
      bgcolor: selected ? SAGE[700] : CREAM[200],
      color: selected ? '#fff' : SAGE[600],
      borderColor: selected ? SAGE[700] : SAGE[300],
      '&:hover': { bgcolor: selected ? SAGE[800] : SAGE[100] },
    }}
  />
);

export default SignupFlow;
